import { Title } from 'react-admin';
import {
  Box, Typography, Card, CardContent, Grid2 as Grid,
  Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress, Chip, Alert, Skeleton,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { differenceInDays, parseISO, format } from 'date-fns';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip as ChartTooltip,
} from 'recharts';
import { useRollup } from '../hooks/useRollup';
import { DEMO_INSTRUCTOR_UTILIZATION } from '../fixtures/analytics-fixture';

const CURRENT_PERIOD = '2026-06';
const TODAY = new Date('2026-07-08');

const isDemoMode = () => import.meta.env.VITE_DEMO_MODE === 'true';

const certColor = (expiry: string) => {
  const days = differenceInDays(parseISO(expiry), TODAY);
  if (days < 0)  return 'error';
  if (days < 30) return 'error';
  if (days < 60) return 'warning';
  return 'success';
};

export function CadreDashboard({ orgPath = '/aetc' }: { orgPath?: string }) {
  const { data, loading } = useRollup(orgPath, 'pass_rate', CURRENT_PERIOD);
  const instructors = isDemoMode() ? DEMO_INSTRUCTOR_UTILIZATION : [];

  const expiringInstructors = instructors.filter((i) => {
    const days = differenceInDays(parseISO(i.certExpiry), TODAY);
    return days < 60;
  });

  const utilizationData = instructors.map((i) => ({
    name: i.name.split(' ')[1],
    sessions: i.sessions,
    capacity: i.capacity,
    utilization: Math.round((i.sessions / i.capacity) * 100),
  }));

  return (
    <Box p={3}>
      <Title title="Cadre Dashboard" />

      {expiringInstructors.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          <strong>{expiringInstructors.length} instructor(s)</strong> have certifications
          expiring within 60 days — review the table below.
        </Alert>
      )}

      {/* Pass rate summary */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              {loading
                ? <Skeleton variant="text" width={80} height={48} sx={{ mx: 'auto' }} />
                : (
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {Math.round((data?.total.avg ?? 0) * 100)}%
                  </Typography>
                )}
              <Typography variant="body2" color="text.secondary">Unit Pass Rate</Typography>
              <Typography variant="caption" color="text.disabled">{CURRENT_PERIOD}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              {loading
                ? <Skeleton variant="text" width={60} height={48} sx={{ mx: 'auto' }} />
                : (
                  <Typography variant="h4" fontWeight={700} color="secondary">
                    {data?.total.count ?? 0}
                  </Typography>
                )}
              <Typography variant="body2" color="text.secondary">Total Attempts</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700}
                color={expiringInstructors.length > 0 ? 'warning.main' : 'success.main'}>
                {expiringInstructors.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Certs Expiring Soon</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Utilization bar chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Instructor Utilization (sessions this period)
          </Typography>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={utilizationData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <ChartTooltip formatter={(v, name) =>
                name === 'utilization' && typeof v === 'number' ? `${v}%` : v} />
              <Bar dataKey="sessions" name="Sessions" fill="#1565c0" radius={[2, 2, 0, 0]} />
              <Bar dataKey="capacity" name="Capacity" fill="#e3f2fd" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Cert table */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Instructor Certifications
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Instructor</TableCell>
                <TableCell>Domains</TableCell>
                <TableCell align="right">Sessions / Cap</TableCell>
                <TableCell align="right">Utilization</TableCell>
                <TableCell align="right">Cert Expiry</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {instructors.map((inst) => {
                const days = differenceInDays(parseISO(inst.certExpiry), TODAY);
                const util = Math.round((inst.sessions / inst.capacity) * 100);
                return (
                  <TableRow key={inst.name} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{inst.name}</Typography>
                    </TableCell>
                    <TableCell>
                      {inst.domains.map((d) => (
                        <Chip key={d} label={d} size="small" sx={{ mr: 0.5, mb: 0.5, fontSize: 10 }} />
                      ))}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{inst.sessions} / {inst.capacity}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={util}
                          sx={{ flex: 1, height: 6, borderRadius: 3 }}
                          color={util >= 90 ? 'success' : util >= 60 ? 'primary' : 'warning'}
                        />
                        <Typography variant="caption">{util}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {format(parseISO(inst.certExpiry), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={days < 0 ? 'Expired' : days < 30 ? `${days}d` : 'Valid'}
                        color={certColor(inst.certExpiry)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
}
